import React from 'react';
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import axios from 'axios';
import { get } from 'lodash';
import { OrganisationDto } from 'models/dtoTypes';
import { OrganisationAttributes } from '../../models';
import ResourceTable, { IColumn } from '../../components/ResourceTable';
import { GetOrganisations, CreateOrganisation } from 'services/openapiClient';
import CreateResourceModal, { ISchema } from '../../dialogs/CreateResourceModal';
import Notice from '../../components/Notice';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import generateNonce from 'utils/generateNonce';
import Link from 'components/Link';
import { useTranslation } from 'react-i18next';

const PREFIX = 'OrganisationsPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',
    display: 'flex',
    gap: '10px',
    margin: theme.spacing(3) + ' 0',

    '& > h2': {
      margin: 0,
      flexGrow: 1,
      flexShrink: 0,
    },

    '& > button': {
      flexShrink: 1,
      flexGrow: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function OrganisationsPage(): JSX.Element {
  const [openCreate, setOpenCreate] = React.useState(false);
  const [nonce, setNonce] = React.useState('');
  const [noticeData, setNoticeData] = React.useState<INoticeData>();

  const { t } = useTranslation('default');

  const columns: IColumn<OrganisationDto>[] = [
    {
      label: t('name'),
      sortIndex: 'name',
      render: (model: OrganisationDto) => {
        return <Link to={`/organisations/${model.id}`} text={model.name} />;
      },
    },
    {
      label: t('display-name'),
      dataIndex: 'displayName',
    },
    {
      label: t('credit'),
      sortIndex: 'remainingCredits',
      render: (organisation) => {
        const { isRestrictedByCredits, remainingCredits = 0 } = organisation;
        return String(isRestrictedByCredits ? remainingCredits : 'Unlimited');
      },
    },
    {
      label: t('user-count'),
      dataIndex: 'usersCount',
    },
    {
      label: t('evaluation'),
      sortIndex: 'isEvaluating',
      render: (organisation) => {
        const isEvaluating = organisation.isEvaluating;
        return (
          <Chip label={isEvaluating ? 'Yes' : 'No'} color={isEvaluating ? 'default' : 'primary'} />
        );
      },
    },
    {
      label: t('SG Auto Assign Enabled'),
      sortIndex: 'smartGovernanceEnabled',
      render: (organisation) => {
        const smartGovernanceEnabled = organisation.smartGovernanceEnabled;
        return (
          <Chip
            label={smartGovernanceEnabled ? 'Yes' : 'No'}
            color={smartGovernanceEnabled ? 'default' : 'primary'}
          />
        );
      },
    },
    {
      label: t('status'),
      sortIndex: 'status',
      render: (organisation) => {
        const isActive = organisation.status === 'Active';
        return (
          <Chip label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'primary' : 'default'} />
        );
      },
    },
  ];

  const schema: ISchema = {
    fields: [
      {
        label: t('name'),
        name: 'name',
        required: true,
        widget: 'text',
        description: '',
      },
      {
        label: t('display-name'),
        name: 'displayName',
        required: true,
        widget: 'text',
        description: '',
      },
      {
        label: 'Has Unlimited Searches?',
        name: 'hasUnlimitedSearches',
        required: false,
        widget: 'checkbox',
        description: '',
      },
      {
        label: t('has-unlimited-credits') + '?',
        name: 'hasUnlimitedCredits',
        required: false,
        widget: 'checkbox',
        description: '',
      },
      {
        label: t('is-under-evaluation') + '?',
        name: 'isEvaluating',
        required: false,
        widget: 'checkbox',
        description: t('coriolis-pdf-report-will-be-generated-with-watermark'),
      },
      {
        label: 'Scoring Report CSV Format',
        name: 'companyReportCsvFormat',
        required: true,
        widget: 'select',
        description: '',
        options: [
          { alias: 'Concise', label: 'Concise' },
          { alias: 'Detailed', label: 'Detailed' },
        ],
      },
      {
        label: 'Billing Type',
        name: 'billingType',
        required: true,
        widget: 'select',
        description: '',
        options: [
          { alias: 'BillOnScoring', label: 'Bill on Scoring' },
          { alias: 'BillOnViewingReport', label: 'Bill on Viewing Report' },
        ],
      },
    ],
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const handleCreate = async (attrs: OrganisationAttributes) => {
    try {
      await CreateOrganisation(attrs);
      setNonce(generateNonce);
      setOpenCreate(false);
      setNoticeData({
        message: 'Successfully created.',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const filterSchema = [
    {
      label: t('name-contains'),
      name: 'name',
      widget: 'text',
    },
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>{t('organisations')}</h2>
        <Button onClick={() => setOpenCreate(true)} variant="contained" startIcon={<AddIcon />}>
          {t('create')}
        </Button>
      </div>
      <ResourceTable
        filterSchema={filterSchema}
        key={nonce}
        columns={columns}
        onFetch={GetOrganisations}
        defaultSort={{
          sortColumn: 'created',
          isDesc: true,
        }}
      />
      <CreateResourceModal<OrganisationAttributes>
        open={openCreate}
        label={t('create-a-new-organisation')}
        schema={schema}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
